import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getWhiteLabel, updateWhiteLabel, pushWhiteLabelBranding } from '@/api/white-labels';
import type { WhiteLabel, UpdateWhiteLabelData } from '@/types/white-label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Tag,
  ArrowLeft,
  Save,
  Star,
  GraduationCap,
  Network,
  ExternalLink,
  Server,
  Info,
} from 'lucide-react';

/**
 * Fiche marque blanche — LECTURE SEULE côté associations.
 *
 * Une marque blanche V2 vit avec son réseau physique : elle se crée avec lui
 * et les écoles s'y rattachent depuis la page du réseau. Seules les URLs des
 * applications restent éditables ici. Les marques blanches V1 documentent
 * l'environnement V1 (jamais modifié).
 */
export function WhiteLabelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [whiteLabel, setWhiteLabel] = useState<WhiteLabel | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pushingBranding, setPushingBranding] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<UpdateWhiteLabelData>({});

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const wlData = await getWhiteLabel(Number(id));
      setWhiteLabel(wlData);
      setFormData({
        name: wlData.name,
        url: wlData.url || '',
        urlAndroid: wlData.urlAndroid || '',
        urlIOS: wlData.urlIOS || '',
      });
    } catch (err) {
      setError('Erreur lors du chargement');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!whiteLabel) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await updateWhiteLabel(whiteLabel.id, formData);
      setSuccess('Marque blanche mise à jour');
      setEditMode(false);
      await fetchData();
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handlePushBranding = async () => {
    if (!whiteLabel) return;
    setPushingBranding(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await pushWhiteLabelBranding(whiteLabel.id);
      setSuccess(result.message);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string; error?: string } } };
      setError(
        axiosError.response?.data?.message ||
          axiosError.response?.data?.error ||
          'Erreur lors du push du branding'
      );
    } finally {
      setPushingBranding(false);
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge variant="default" className="bg-green-100 text-green-700">Actif</Badge>;
      case 'INACTIVE':
        return <Badge variant="secondary">Inactif</Badge>;
      case 'SUSPENDED':
        return <Badge variant="destructive">Suspendu</Badge>;
      default:
        return <Badge variant="outline">-</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Chargement...
      </div>
    );
  }

  if (!whiteLabel) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Marque blanche non trouvée.
      </div>
    );
  }

  // Check if this is a V2 white label
  const isV2 = !!whiteLabel.v2TenantGroupId;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/white-labels')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Tag className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight text-foreground">
                  {whiteLabel.name}
                </h1>
                {whiteLabel.isDefault && (
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                    <Star className="h-3 w-3 mr-1" />
                    Défaut
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                ID: {whiteLabel.id}
              </p>
            </div>
          </div>
        </div>
        {!editMode && (
          <Button onClick={() => setEditMode(true)}>
            Modifier
          </Button>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2">&times;</button>
        </div>
      )}
      {success && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-green-700">
          {success}
          <button onClick={() => setSuccess(null)} className="ml-2">&times;</button>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* V2 Info - only show for V2 white labels */}
        {isV2 ? (
          <div className="rounded-lg border bg-card p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-muted-foreground" />
              <h2 className="font-semibold text-lg">Tenant Group V2</h2>
            </div>

            <div className="grid gap-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Statut</span>
                {getStatusBadge(whiteLabel.v2Status)}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Host</span>
                <span className="font-mono text-sm">{whiteLabel.v2Host || '-'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Tenant Group ID</span>
                <span className="font-mono text-xs truncate max-w-[200px]" title={whiteLabel.v2TenantGroupId || ''}>
                  {whiteLabel.v2TenantGroupId || '-'}
                </span>
              </div>
              {whiteLabel.tenantCount !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Nb. Tenants</span>
                  <span className="font-medium">{whiteLabel.tenantCount}</span>
                </div>
              )}
            </div>

            <div className="pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePushBranding}
                disabled={pushingBranding || whiteLabel.isDefault}
              >
                {pushingBranding ? 'Envoi en cours...' : 'Pousser le branding vers V2'}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Envoie les images d'une école du réseau (couverture, logo, favicon)
                dans les settings du tenant group V2.
              </p>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border bg-card p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-muted-foreground" />
              <h2 className="font-semibold text-lg">Marque blanche V1</h2>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <p>
                Marque blanche V1 (environnement legacy, jamais modifié). Au passage en V2 de
                l'école, celle-ci devra rejoindre un réseau physique.
              </p>
            </div>
          </div>
        )}

        {/* URLs */}
        <div className="rounded-lg border bg-card p-6 space-y-4">
          <h2 className="font-semibold text-lg">URLs</h2>

          {editMode ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="url">URL Web</Label>
                <Input
                  id="url"
                  type="url"
                  value={formData.url || ''}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="urlAndroid">URL Android</Label>
                <Input
                  id="urlAndroid"
                  type="url"
                  value={formData.urlAndroid || ''}
                  onChange={(e) => setFormData({ ...formData, urlAndroid: e.target.value })}
                  placeholder="https://play.google.com/..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="urlIOS">URL iOS</Label>
                <Input
                  id="urlIOS"
                  type="url"
                  value={formData.urlIOS || ''}
                  onChange={(e) => setFormData({ ...formData, urlIOS: e.target.value })}
                  placeholder="https://apps.apple.com/..."
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditMode(false)}>
                  Annuler
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid gap-3">
              <div>
                <span className="text-sm text-muted-foreground">Web</span>
                {whiteLabel.url ? (
                  <a
                    href={whiteLabel.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline"
                  >
                    {whiteLabel.url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <p className="text-muted-foreground">-</p>
                )}
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Android</span>
                {whiteLabel.urlAndroid ? (
                  <a
                    href={whiteLabel.urlAndroid}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline truncate"
                  >
                    {whiteLabel.urlAndroid}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <p className="text-muted-foreground">-</p>
                )}
              </div>
              <div>
                <span className="text-sm text-muted-foreground">iOS</span>
                {whiteLabel.urlIOS ? (
                  <a
                    href={whiteLabel.urlIOS}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline truncate"
                  >
                    {whiteLabel.urlIOS}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <p className="text-muted-foreground">-</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Association — lecture seule */}
        <div className="rounded-lg border bg-card p-6 space-y-4 md:col-span-2">
          <h2 className="font-semibold text-lg">Association</h2>

          {whiteLabel.schoolNetwork ? (
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200">
                <Network className="h-3 w-3 mr-1" />
                Réseau physique
              </Badge>
              <Link
                to={`/school-networks/${whiteLabel.schoolNetwork.id}`}
                className="font-medium hover:underline text-primary"
              >
                {whiteLabel.schoolNetwork.name}
              </Link>
              <span className="text-sm text-muted-foreground">
                — la marque blanche se gère depuis la page du réseau
              </span>
            </div>
          ) : whiteLabel.school ? (
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
              <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">
                <GraduationCap className="h-3 w-3 mr-1" />
                École (V1)
              </Badge>
              <Link
                to={`/schools/${whiteLabel.school.code}`}
                className="font-medium hover:underline text-primary"
              >
                {whiteLabel.school.name} ({whiteLabel.school.code})
              </Link>
              {whiteLabel.activeEnvironment && (
                <Badge
                  variant="outline"
                  className={
                    whiteLabel.activeEnvironment === 'v2'
                      ? 'bg-purple-50 text-purple-700 border-purple-200'
                      : 'bg-gray-50 text-gray-700 border-gray-200'
                  }
                >
                  {whiteLabel.activeEnvironment.toUpperCase()}
                </Badge>
              )}
            </div>
          ) : (
            <div className="flex items-start gap-2 p-3 bg-muted/50 border rounded-lg text-sm text-muted-foreground">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                Marque blanche sans association. Les associations ne se modifient plus ici : les
                marques blanches V2 naissent avec leur réseau physique, et les écoles se rattachent
                depuis la page du réseau.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
